import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  useTheme,
  Tooltip,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from '../../types';
import UniversalBadge from '../common/UniversalBadge';

// Sort types
type SortField = 'name' | 'status' | 'createdAt' | 'userCount';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface OrganizationTableProps {
  organizations: Organization[];
  selectedItems: string[];
  selectedRowId: string | null;
  searchTerm: string;
  statusFilter: string;
  isWide: boolean;
  onSelectItem: (event: React.ChangeEvent<HTMLInputElement>, id: string) => void;
  onSelectAll: (checked: boolean) => void;
  onContextMenu: (event: React.MouseEvent, organizationId: string) => void;
}

const OrganizationTable: React.FC<OrganizationTableProps> = ({
  organizations,
  selectedItems,
  selectedRowId,
  searchTerm,
  statusFilter,
  isWide,
  onSelectItem,
  onSelectAll,
  onContextMenu
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'name',
    direction: 'asc'
  });

  // Filter and sort organizations
  const filteredAndSortedOrganizations = useMemo(() => {
    if (!organizations || !Array.isArray(organizations)) {
      return [];
    }
    
    // Filter first
    const filtered = organizations.filter(org => {
      const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (org.description && org.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const userCount = org.users?.length || (org as any).userCount || 0;
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'active' && userCount > 0) ||
                           (statusFilter === 'inactive' && userCount === 0);
      
      return matchesSearch && matchesStatus;
    });

    // Then sort
    return filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortConfig.field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status || 'active';
          bValue = b.status || 'active';
          break;
        case 'userCount':
          aValue = a.users?.length || (a as any).userCount || 0;
          bValue = b.users?.length || (b as any).userCount || 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || '').getTime();
          bValue = new Date(b.createdAt || '').getTime();
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [organizations, searchTerm, statusFilter, sortConfig]);

  const handleRowClick = (organizationId: string) => {
    // Navigiere innerhalb der SPA ohne kompletten Reload
    navigate(`/admin/${organizationId}`);
  };

  const isSelected = (id: string): boolean => {
    return location.pathname.includes(`/admin/${id}`);
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getUserCount = (organization: any): number => {
    return organization.users?.length || organization.userCount || 0;
  };

  const getServiceProviderCount = (organization: any): number => {
    return organization.serviceProviders?.length || organization.serviceProviderCount || 0;
  };

  const getServicesCount = (organization: any): number => {
    // Summe aller Verträge von allen Dienstleistern
    if (organization.serviceProviders?.length) {
      return organization.serviceProviders.reduce((total: number, provider: any) => {
        return total + (provider.contractCount || provider.contracts?.length || 0);
      }, 0);
    }
    // Fallback für API-Daten die bereits die Gesamtzahl liefern
    return organization.totalServices || 0;
  };

  const getServiceLimitDisplay = (organization: any) => {
    const currentServices = getServicesCount(organization);
    const maxServices = organization.maxServices;
    
    if (!maxServices) {
      // Kein Limit gesetzt - zeige nur aktuelle Anzahl
      return {
        display: currentServices.toString(),
        isAtLimit: false,
        isNearLimit: false
      };
    }
    
    const isAtLimit = currentServices >= maxServices;
    const isNearLimit = currentServices >= maxServices * 0.8; // 80% des Limits
    
    return {
      display: `${currentServices}/${maxServices}`,
      isAtLimit,
      isNearLimit
    };
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <TableContainer >
      <Table >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedItems.length === filteredAndSortedOrganizations.length && filteredAndSortedOrganizations.length > 0}
                onChange={(e) => onSelectAll(e.target.checked)}
                data-testid="admin-org-table-select-all-checkbox"
                sx={{ padding: '0.5rem' }}
              />
            </TableCell>
            <TableCell 
              sx={{ 
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('name')}
              data-testid="admin-org-table-header-name"
            >
              Name {sortConfig.field === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            {isWide && (
              <>
                <TableCell 
                  sx={{ 
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '1rem',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                  onClick={() => handleSort('userCount')}
                  data-testid="admin-org-table-header-usercount"
                >
                  Benutzer {sortConfig.field === 'userCount' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </TableCell>
                <TableCell sx={{ fontSize: '1rem' }} data-testid="admin-org-table-header-providers">Dienstleister</TableCell>
                <TableCell sx={{ fontSize: '1rem' }} data-testid="admin-org-table-header-services">Dienstleistungen</TableCell>
                <TableCell 
                  sx={{ 
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '1rem',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                  onClick={() => handleSort('createdAt')}
                  data-testid="admin-org-table-header-created"
                >
                  Erstellt {sortConfig.field === 'createdAt' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </TableCell>
              </>
            )}
            <TableCell 
              sx={{ 
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('status')}
              data-testid="admin-org-table-header-status"
            >
              Status {sortConfig.field === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSortedOrganizations.map((organization: Organization) => (
            <TableRow
              key={organization._id}
              hover
              onClick={() => handleRowClick(organization._id)}
              onContextMenu={(e) => onContextMenu(e, organization._id)}
              data-testid={`admin-org-table-row-${organization._id}`}
              sx={{
                cursor: 'pointer',
                height: '4.25rem',
                bgcolor: isSelected(organization._id) 
                  ? 'primary.main' 
                  : selectedRowId === organization._id 
                    ? 'rgba(0, 0, 0, 0.04)' 
                    : 'inherit',
                '&:hover': {
                  bgcolor: isSelected(organization._id)
                    ? 'primary.dark'
                    : selectedRowId === organization._id 
                      ? 'rgba(0, 0, 0, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)'
                },
                borderLeft: isSelected(organization._id) ? '4px solid' : '4px solid transparent',
                borderLeftColor: isSelected(organization._id) ? 'primary.blue' : 'transparent',
                transition: 'background-color 0.2s ease'
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedItems.includes(organization._id)}
                  onChange={(e) => onSelectItem(e, organization._id)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`admin-org-table-row-${organization._id}-checkbox`}
                  sx={{
                    padding: '0.5rem',
                    color: 'primary.blue',
                    '&.Mui-checked': {
                      color: 'primary.blue',
                    }
                  }}
                />
              </TableCell>
              
              <TableCell sx={{ 
                fontSize: '1rem',
                fontWeight: 400
              }}>
                <Tooltip title={organization.name} arrow>
                  <Box sx={{ 
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    maxWidth: isWide ? '200px' : '180px',
                    lineHeight: 1.2,
                    maxHeight: '2.4em' // 2 lines * 1.2 line-height
                  }}>
                    {organization.name}
                  </Box>
                </Tooltip>
              </TableCell>

              {isWide && (
                <>
                  <TableCell>
                    {getUserCount(organization)}
                  </TableCell>

                  <TableCell>
                    {getServiceProviderCount(organization)}
                  </TableCell>

                  <TableCell>
                    {(() => {
                      const limitInfo = getServiceLimitDisplay(organization);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: '1rem',
                              color: limitInfo.isAtLimit 
                                ? 'error.main' 
                                : limitInfo.isNearLimit 
                                  ? 'warning.main' 
                                  : 'text.primary',
                              fontWeight: limitInfo.isAtLimit || limitInfo.isNearLimit ? 600 : 400
                            }}
                          >
                            {limitInfo.display}
                          </Typography>
                          {limitInfo.isAtLimit && (
                            <Tooltip title="Service-Limit erreicht">
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'error.main',
                                  flexShrink: 0
                                }}
                              />
                            </Tooltip>
                          )}
                          {limitInfo.isNearLimit && !limitInfo.isAtLimit && (
                            <Tooltip title="Nahe am Service-Limit (80%)">
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'warning.main',
                                  flexShrink: 0
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>

                  <TableCell>
                    {formatDate(organization.createdAt)}
                  </TableCell>
                </>
              )}

              <TableCell>
                <UniversalBadge 
                  type="organization-status" 
                  value={organization.status || 'active'} 
                  data-testid={`admin-org-table-row-${organization._id}-status-badge`}
                />
              </TableCell>
            </TableRow>
          ))}
          
          {filteredAndSortedOrganizations.length === 0 && (
            <TableRow>
              <TableCell colSpan={isWide ? 7 : 3} sx={{ textAlign: 'center', py: 3 }} data-testid="admin-org-table-empty-state">
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || statusFilter !== 'all' 
                    ? t('admin:messages.noOrganizationsFound') 
                    : t('admin:messages.noOrganizationsYet')
                  }
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrganizationTable; 